import Sentry, { featureTags } from '@/services/sentry'
import { findInsteadClashes, reportSettingHookProblem } from '@/business-logic/setting-hooks/settingHookDiscovery'

const NAMED_PARTS = ['methods', 'computed']

// Depth-first list of the vnodes under `root` (itself included) that match `predicate`, for render hooks.
export const findVNodes = (root, predicate) => {
  if (!root) return []
  const own = predicate(root) ? [root] : []
  return own.concat(...(root.children || []).map((child) => findVNodes(child, predicate)))
}

const toPascalCase = (tag) => tag.replace(/(?:^|-)([a-z])/g, (_, letter) => letter.toUpperCase())

// The child component vnodes under `root` written as one of `names` in the template (`<VoteCount>` or `<vote-count>`).
export const findComponentVNodes = (root, names) => findVNodes(
  root,
  (node) => Boolean(node.componentOptions) && names.includes(toPascalCase(node.componentOptions.tag || '')),
)

const findMissingRefs = (vnode, refs) => {
  const withRef = findVNodes(vnode, (node) => Boolean(node.data && node.data.ref))
  const rendered = new Set(withRef.map((node) => node.data.ref))
  return refs.filter((ref) => !rendered.has(ref))
}

const runHandler = (handler, input, {
  setting, part, name, context, isOriginalError = () => false,
}) => {
  try {
    return { ok: true, value: handler.call(context.vm, input, context) }
  } catch (error) {
    if (!isOriginalError(error)) {
      Sentry.captureException(error, {
        tags: featureTags({ featureFlag: setting.featureFlag, featureName: `setting-hook:${setting.key}`, taskId: 'PRRW-313' }),
        extra: { part, name },
      })
    }
    return { ok: false }
  }
}

// Each `instead` wraps the next one in. A hook that throws falls back to the next one, or, once it has called through,
// to that call's outcome: its result, or its error, which stays the caller's to handle.
const chainInstead = (entries, callOriginal, { part, name, contextFor }) => entries.reduce((next, entry) => {
  const { setting, handlers } = entry
  if (!handlers.instead) return next
  return (innerArgs) => {
    let through = null
    const callNext = (...nextArgs) => {
      try {
        through = { value: next(nextArgs) }
        return through.value
      } catch (error) {
        through = { failed: true, error }
        throw error
      }
    }
    const run = runHandler(handlers.instead, innerArgs, {
      setting,
      part,
      name,
      context: { ...contextFor(setting, innerArgs), original: callNext },
      isOriginalError: (error) => Boolean(through && through.failed && through.error === error),
    })
    if (run.ok) return run.value
    if (!through) return next(innerArgs)
    if (through.failed) throw through.error
    return through.value
  }
}, callOriginal)

// One wrapper per original function: it reads `this` at call time, so every instance of the component shares it.
const wrapFunction = (original, entries, { part, name, readSetting }) => function settingHooked(...callArgs) {
  const contextFor = (setting, args) => ({ value: readSetting(setting.key), vm: this, args })
  let args = callArgs
  entries.forEach(({ setting, handlers }) => {
    if (!handlers.before) return
    const run = runHandler(handlers.before, args, {
      setting, part, name, context: contextFor(setting, args),
    })
    if (run.ok && Array.isArray(run.value)) args = run.value
  })
  let result = chainInstead(entries, (finalArgs) => original.apply(this, finalArgs), { part, name, contextFor })(args)
  entries.forEach(({ setting, handlers }) => {
    if (!handlers.after) return
    const run = runHandler(handlers.after, result, {
      setting, part, name, context: contextFor(setting, args),
    })
    if (run.ok) result = run.value
  })
  return result
}

const collectTargets = (entries, part) => {
  const targets = {}
  entries.forEach(({ setting, spec }) => {
    Object.entries(spec[part] || {}).forEach(([name, handlers]) => {
      targets[name] = [...(targets[name] || []), { setting, handlers }]
    })
  })
  return targets
}

const EMPTY_PLAN = Object.freeze({ methods: {}, computed: {}, render: [] })

/**
 * Turns a hook source (see settingHookSources.js) into a global mixin. A component whose `name` may be hooked gets
 * its methods, computed getters and render wrapped before Vue reads them; every other component pays one lookup.
 * Each wrapper looks up the component's hooks when it runs, so hooks that load later apply without a remount.
 */
export const createSettingHooksMixin = (source, readSetting) => {
  const reported = new Set()
  const reportOnce = (message) => {
    if (reported.has(message)) return
    reported.add(message)
    reportSettingHookProblem(message)
  }
  // A render hook runs only while every ref it names is rendered (a v-if may leave one out); a ref or child component
  // removed from the template is caught by the settings-folder unit test, which checks each one exists in the component.
  const guardRefs = ({ refs = [], after }) => function guardedRenderAfter(vnode, context) {
    return findMissingRefs(vnode, refs).length ? vnode : after.call(this, vnode, context)
  }
  const plans = new WeakMap()
  const planFor = (entries, componentName) => {
    if (!entries.length) return EMPTY_PLAN
    if (!plans.has(entries)) {
      findInsteadClashes(entries).forEach(({ name, settings }) => {
        reportOnce(`Setting hooks clash: ${settings.join(', ')} all replace ${componentName}.${name}, so only one can run`)
      })
      plans.set(entries, {
        methods: collectTargets(entries, 'methods'),
        computed: collectTargets(entries, 'computed'),
        render: entries
          .filter(({ spec }) => spec.render)
          .map(({ setting, spec }) => ({ setting, handlers: { after: guardRefs(spec.render) } })),
      })
    }
    return plans.get(entries)
  }
  // Caches are keyed by the hook list too: a mixin method shared by two components gets each one's own hooks.
  const cacheFor = (caches, key) => {
    if (!caches.has(key)) caches.set(key, new WeakMap())
    return caches.get(key)
  }
  const wrapped = new WeakMap()
  const wrapOnce = (original, entries, details) => {
    const cache = cacheFor(wrapped, entries)
    if (!cache.has(original)) cache.set(original, wrapFunction(original, entries, { ...details, readSetting }))
    return cache.get(original)
  }
  // One hook point per hooked target and original function, shared by every instance of the component.
  const points = new Map()
  const hookPoint = (original, componentName, part, name) => {
    const cache = cacheFor(points, `${componentName}|${part}|${name}`)
    if (!cache.has(original)) {
      const point = function settingHookPoint(...args) {
        const plan = planFor(source.entriesFor(componentName), componentName)
        const entries = part === 'render' ? plan.render : plan[part][name]
        if (!entries || !entries.length) return original.apply(this, args)
        return wrapOnce(original, entries, { part, name }).apply(this, args)
      }
      // Keep the markers other mixins put on a method (BroadcastMixin reads `__isBroadcast` and `__originalFn`).
      Object.defineProperties(point, Object.fromEntries(Object.entries(Object.getOwnPropertyDescriptors(original))
        .filter(([key]) => !['length', 'name', 'prototype', 'arguments', 'caller'].includes(key))))
      cache.set(original, point)
    }
    return cache.get(original)
  }
  const reportMissing = (componentName, part, name) => {
    reportOnce(`Setting hook skipped: component "${componentName}" has no ${part} "${name}"`)
  }
  // The wrapped methods/computed object is built once per component definition and reused by every instance.
  const builtParts = new Map()
  const wrapPart = (sourcePart, componentName, part, names) => {
    const built = cacheFor(builtParts, `${componentName}|${part}`)
    if (!built.has(sourcePart)) {
      const next = { ...sourcePart }
      names.forEach((name) => {
        const definition = sourcePart[name]
        const getter = typeof definition === 'function' ? definition : definition && definition.get
        if (typeof getter !== 'function') return
        const hooked = hookPoint(getter, componentName, part, name)
        next[name] = typeof definition === 'function' ? hooked : { ...definition, get: hooked }
      })
      built.set(sourcePart, next)
    }
    return built.get(sourcePart)
  }
  const reportMissingTargets = (componentName, options, plan) => {
    NAMED_PARTS.forEach((part) => Object.keys(plan[part]).forEach((name) => {
      const definition = (options[part] || {})[name]
      const getter = typeof definition === 'function' ? definition : definition && definition.get
      if (typeof getter !== 'function') reportMissing(componentName, part, name)
    }))
    if (plan.render.length && typeof options.render !== 'function') reportMissing(componentName, 'render', 'function')
  }

  return {
    beforeCreate() {
      const componentName = this.$options.name
      if (!componentName || !source.componentNames.has(componentName)) return
      const options = this.$options
      const plan = planFor(source.entriesFor(componentName), componentName)
      reportMissingTargets(componentName, options, plan)
      // A lazy source wraps every function, as the hooks it will load are not known yet.
      NAMED_PARTS.forEach((part) => {
        const names = source.lazy ? Object.keys(options[part] || {}) : Object.keys(plan[part])
        if (names.length) options[part] = wrapPart(options[part] || {}, componentName, part, names)
      })
      const hookRender = source.lazy || plan.render.length
      if (hookRender && typeof options.render === 'function') {
        options.render = hookPoint(options.render, componentName, 'render', componentName)
      }
    },
  }
}
